import React, { useEffect, useState } from "react";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(RadialLinearScale, PointElement, LineElement, Tooltip, Legend);

// === Color schema ===
const GREEN = "#2e7d32";   // primary trend
const ORANGE = "#fb8c00";  // secondary/comparison (future)
const BLUE = "#2196f3";    // reference/avg (future)

const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 50vh;
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #ccc;
`;

const CustomerProfiling = ({ salesData }) => {
  const [selectedContractor, setSelectedContractor] = useState("");
  const [contractors, setContractors] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [chartData, setChartData] = useState(null);

  const monthNames = [
    "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
    "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"
  ];

  const calculateTransactionValue = (row) =>
    parseFloat(row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22) || 0;

  useEffect(() => {
    if (!salesData || salesData.length === 0) return;

    const years = [...new Set(salesData.map((sale) => parseInt(sale.Rok, 10)))].sort((a, b) => b - a);
    setAvailableYears(years);
    if (!years.includes(selectedYear)) setSelectedYear(years[0]);

    const uniqueContractors = [...new Set(salesData.map((sale) => sale.NazwaKontrahenta).filter(Boolean))];
    setContractors(uniqueContractors);
    if (!selectedContractor && uniqueContractors.length > 0) setSelectedContractor(uniqueContractors[0]);
  }, [salesData]);

  useEffect(() => {
    if (selectedYear) {
      const months = [...new Set(
        salesData
          .filter((sale) => parseInt(sale.Rok, 10) === selectedYear)
          .map((sale) => parseInt(sale.Miesiac, 10))
      )].sort((a, b) => a - b);
      setAvailableMonths(months);
    }
  }, [selectedYear, salesData]);

  useEffect(() => {
    if (!selectedContractor || !selectedYear) return;

    const filteredData = salesData.filter(
      (sale) =>
        sale.NazwaKontrahenta === selectedContractor &&
        parseInt(sale.Rok, 10) === selectedYear &&
        (selectedMonth === "all" || parseInt(sale.Miesiac, 10) === selectedMonth)
    );

    if (filteredData.length === 0) {
      setChartData(null);
      return;
    }

    const transactionValues = filteredData.map(calculateTransactionValue);
    const totalTransactions = transactionValues.length;
    const totalSpend = transactionValues.reduce((sum, val) => sum + val, 0);
    const avgTransactionValue = totalSpend / totalTransactions;
    const highestTransaction = Math.max(...transactionValues);
    const lowestTransaction = Math.min(...transactionValues);

    setChartData({
      labels: [
        "Częstotliwość zakupów",
        "Łączna kwota",
        "Średnia wartość transakcji",
        "Najwyższa transakcja",
        "Najniższa transakcja",
      ],
      datasets: [
        {
          label: `Profil: ${selectedContractor}`,
          data: [
            totalTransactions,
            totalSpend,
            avgTransactionValue,
            highestTransaction,
            lowestTransaction,
          ],
          // color schema applied
          borderColor: GREEN,
          backgroundColor: "rgba(46, 125, 50, 0.15)",
          pointBackgroundColor: GREEN,
          pointBorderColor: GREEN,
          pointHoverBackgroundColor: GREEN,
          pointHoverBorderColor: GREEN,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
        },
      ],
    });
  }, [selectedContractor, selectedYear, selectedMonth, salesData]);

  return (
    <ChartWrapper>
      <TitleWrapper>
        Profilowanie Klienta
        <div data-tooltip-id="profiling-tooltip" style={{ cursor: "pointer" }}>
          <Info size={18} />
        </div>
      </TitleWrapper>

      <InfoTooltip
        id="profiling-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Profil klienta</strong><br/>
            Wykres radarowy pokazuje:<br/>
            ilość, sumę, średnią oraz<br/>
            najwyższe i najniższe transakcje<br/>
            wybranego kontrahenta.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Kontrahent:</label>
          <Select value={selectedContractor} onChange={(e) => setSelectedContractor(e.target.value)}>
            {contractors.map((contractor) => (
              <option key={contractor} value={contractor}>{contractor}</option>
            ))}
          </Select>
        </div>
        <div>
          <label>Rok:</label>
          <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}>
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </div>
        <div>
          <label>Miesiąc:</label>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value === "all" ? "all" : parseInt(e.target.value, 10))}
          >
            <option value="all">Cały rok</option>
            {availableMonths.map((month) => (
              <option key={month} value={month}>{monthNames[month - 1]}</option>
            ))}
          </Select>
        </div>
      </FiltersWrapper>

      {chartData ? (
        <Radar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              r: {
                beginAtZero: true,
                angleLines: { color: "rgba(0,0,0,0.08)" },
                grid: { color: "rgba(0,0,0,0.08)" },
                pointLabels: { font: { size: 11 } },
                suggestedMin: 0,
              },
            },
            plugins: {
              legend: { position: "top" },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  label: (context) =>
                    `${context.label}: ${Number(context.raw || 0).toLocaleString("pl-PL", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`,
                },
              },
            },
          }}
        />
      ) : (
        <p style={{ textAlign: "center" }}>Brak danych dla wybranych filtrów.</p>
      )}
    </ChartWrapper>
  );
};

export default CustomerProfiling;
